import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./AddLosPluginModal.module.css";
import { Modal } from "shared/Modal";
import CheckBoxItem from "DreamStream/Shared/CheckBoxItem";
import ModalActionBar from "shared/ModalActionBar";
import { useGroupPlugins } from "../../usePlugins";

/**
 * Modal used for adding a LOS plugin.
 */

export function AddLosPluginModal({ isOpen, onClose, onClick }) {
  const [pinLevel, setPinLevel] = useState("");
  const [sync, setSync] = useState(false);
  const [errors, setErrors] = useState({
    selectedTarget: "",
    selectedPlugee: "",
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { groupPlugins, loading } = useGroupPlugins();
  const [selectedPlugee, setSelectedPlugee] = useState("");
  const [selectedTarget, setSelectedTarget] = useState("");
  const [newPlugee, setNewPlugee] = useState([]);
  const [newTarget, setNewTarget] = useState([]);

  const [newPlugeeFilter, setNewPlugeeFilter] = useState("");
  const [newTargetFilter, setNewTargetFilter] = useState("");
  const [openDropDown, setOpenDropdown] = useState(false);
  const [openTargetDropDown, setOpenTargetDropdown] = useState(false);

  const ref = useRef(null);

  const handleDropdownClick = () => {
    setOpenDropdown(!openDropDown);
    ref.current.focus();
  };

  const groupsData = groupPlugins?.filter(
    (org) => org.groups.length > 0 && org.pinLevel !== 0
  );
  const sortedData = groupsData?.reduce((acc, org) => {
    org.groups.forEach((group) => {
      const newOrgEntry = { ...org, groups: [group] };
      acc.push(newOrgEntry);
    });
    return acc;
  }, []);

  // Reset modal when closed
  useEffect(() => {
    if (!isOpen) {
      setSync(false);
      setSelectedPlugee("");
      setSelectedTarget("");
      setErrors({ selectedTarget: "", selectedPlugee: "" });
      setPinLevel("");
    }
  }, [isOpen]);

  function filteredPlugeeData() {
    const suggestGroup = sortedData.filter(
      (org) =>
        (org.groups &&
          org.groups.some((group) =>
            group.groupName
              .toLowerCase()
              .includes(newPlugeeFilter.trim().toLowerCase())
          )) ||
        (org.organizationName &&
          org.organizationName
            .toLowerCase()
            .includes(newPlugeeFilter.trim().toLowerCase())) ||
        (org.iboNumber &&
          String(org.iboNumber).includes(newPlugeeFilter.trim()))
    );

    return suggestGroup;
  }

  function filteredTargetData() {
    const suggestGroup = sortedData.filter(
      (org) =>
        (org.groups &&
          org.groups.some((group) =>
            group.groupName
              .toLowerCase()
              .includes(newTargetFilter.trim().toLowerCase())
          )) ||
        (org.organizationName &&
          org.organizationName
            .toLowerCase()
            .includes(newTargetFilter.trim().toLowerCase())) ||
        (org.iboNumber &&
          String(org.iboNumber).includes(newTargetFilter.trim()))
    );

    const finalGroups = suggestGroup.filter(
      (group) => group.pinLevel === pinLevel
    );

    return finalGroups;
  }

  const groupSearchSelect = (group, isPrimary) => {
    const display = [
      group.iboNumber,
      group.organizationName,
      group.groups[0].groupName,
    ];

    if (isPrimary) {
      setNewPlugee(group);
      setSelectedPlugee(display);
      setPinLevel(group.pinLevel);
      setNewPlugeeFilter("");
      setOpenDropdown(false);
      setOpenTargetDropdown(false);
    }

    if (!isPrimary) {
      setNewTarget(group);
      setSelectedTarget(display);
      setPinLevel("");
      setNewTargetFilter("");
      setOpenDropdown(false);
      setOpenTargetDropdown(false);
    }
  };

  function validateForm() {
    let tempErrors = {};

    if (selectedPlugee.length < 1) {
      tempErrors = {
        ...tempErrors,
        selectedPlugee: "Please enter Plugee.",
      };
    }

    if (selectedTarget.length < 1) {
      tempErrors = {
        ...tempErrors,
        selectedTarget: "Please enter Target.",
      };
    }

    if (newPlugee.iboNumber === newTarget.iboNumber) {
      tempErrors = {
        ...tempErrors,
        selectedTarget: "Cannot plug IBO into the same IBO.",
      };
    }

    const matchedIboData = groupPlugins?.filter(
      (data) => data.iboNumber === Number(newPlugee.iboNumber)
    );

    if (matchedIboData && matchedIboData.length > 0) {
      const existingPlugin = matchedIboData.some((data) =>
        data.plugins.some((plugin) =>
          plugin.groups.some(
            (group) => group.groupId === newTarget.groups[0].groupId
          )
        )
      );
      if (existingPlugin) {
        tempErrors = {
          ...tempErrors,
          selectedTarget: "This group already exists. Add a different group.",
        };
      }
    }

    setErrors(tempErrors);
    const hasErrors = Object.keys(tempErrors).length > 0;

    if (hasErrors) {
      return false;
    } else {
      return true;
    }
  }

  function handleCreateLosPlugin() {
    let isFormValid = validateForm();

    if (isFormValid) {
      const singleArray = [newTarget.groups[0]];
      const pluginData = {
        plugee: {
          name: newTarget.name,
          iboNumber: newTarget.iboNumber,
          pinLevel: newTarget.pinLevel,
          organizationId: newTarget.organizationId,
          organizationName: newTarget.organizationName,

          groups: singleArray,
        },
        pluggedIntoPlugins: {
          iboNumber: newPlugee.iboNumber,
          pinLevel: newPlugee.pinLevel,
          organizationId: newPlugee.organizationId,
        },
        sync: sync,
      };

      onClick(pluginData);
      setIsLoadingData(!isLoadingData);
    }
  }

  return (
    <div className={styles.container}>
      <Modal
        isOpen={isOpen}
        title={"ADD LOS PLUGIN"}
        closeBtn="none"
        className={styles.modal}
        onClose={onClose}
        children={
          <>
            <p className={styles.description}>
              Plug an IBO into another's downline.
            </p>

            <div className={styles.inputContainer}>
              <div className={styles.inputItem}>
                <div className={styles.label}> Plugee: </div>
                <div className={styles.dropDownContainer}>
                  <div
                    className={styles.selected}
                    onClick={handleDropdownClick}
                  >
                    <input
                      type="text"
                      ref={ref}
                      placeholder={
                        selectedPlugee.length > 0
                          ? selectedPlugee.join(" > ")
                          : "Search by Organization, Group, or IBO #"
                      }
                      value={newPlugeeFilter}
                      onChange={(e) => {
                        setNewPlugeeFilter(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {!loading && openDropDown && (
                  <div className={styles.selectOptions}>
                    <>
                      {filteredPlugeeData(sortedData, newPlugeeFilter).map(
                        (item, _id) => (
                          <div
                            key={item._id}
                            className={styles.selectOption}
                            onClick={() => {
                              groupSearchSelect(item, true);
                            }}
                          >
                            <span className={styles.groupListItem}>
                              {item.iboNumber} &gt; {item.organizationName} &gt;{" "}
                              {item.groups[0].groupName}
                            </span>
                          </div>
                        )
                      )}
                    </>
                  </div>
                )}
                <span className={styles.errorOrgMsg}>
                  {errors.selectedPlugee}
                </span>
              </div>
              <div className={styles.inputItem}>
                <div className={styles.label}> Target: </div>

                <div className={styles.dropDownContainer}>
                  <div
                    className={styles.selected}
                    onClick={() => setOpenTargetDropdown(!openTargetDropDown)}
                  >
                    <input
                      type="text"
                      placeholder={
                        selectedTarget.length > 0
                          ? selectedTarget.join(" > ")
                          : "Search by Organization, Group, or IBO #"
                      }
                      value={newTargetFilter}
                      onChange={(e) => {
                        setNewTargetFilter(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {!loading && openTargetDropDown && (
                  <div className={styles.targetOptions}>
                    {pinLevel ? (
                      <>
                        {filteredTargetData(sortedData, newTargetFilter).map(
                          (item, _id) => (
                            <div
                              key={item._id}
                              className={styles.targetOption}
                              onClick={() => {
                                groupSearchSelect(item, false);
                              }}
                            >
                              <span className={styles.groupListItem}>
                                {item.iboNumber} &gt; {item.organizationName}{" "}
                                &gt; {item.groups[0].groupName}
                              </span>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <div className={styles.enterText}>
                        Select Plugee to see results
                      </div>
                    )}
                  </div>
                )}
                <span className={styles.errorGroupMsg}>
                  {errors.selectedTarget}
                </span>
                <br></br>
              </div>
            </div>

            <div className={styles.item}>
              <CheckBoxItem
                name={"Sync"}
                checked={sync}
                onClick={() => setSync(!sync)}
              />
            </div>

            <ModalActionBar
              children={
                <div className={styles.buttonBar}>
                  <button type="button" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="button" onClick={() => handleCreateLosPlugin()}>
                    Add
                  </button>
                </div>
              }
            />
          </>
        }
      />
    </div>
  );
}

AddLosPluginModal.propTypes = {
  /**
   * Getter to determine if the the modal is displayed.
   */
  isOpen: PropTypes.bool,
  /**
   * Callback function for when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * 	Function called when the add btn is clicked
   */
  onClick: PropTypes.func,
};
